import React, { useState } from "react";
import { Container, Form, Button, Card, Alert } from "react-bootstrap";
import api from "../api/api";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../context/AlertContext";

const CreateClient = () => {
	const { showAlert } = useAlert();
	const navigate = useNavigate();

	const [step, setStep] = useState("email");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [date_of_birth, setdate_of_birth] = useState("");
	const [message, setMessage] = useState("");
	const [messageVariant, setMessageVariant] = useState("info");
	const [emailError, setEmailError] = useState("");
	const [isCheckingEmail, setIsCheckingEmail] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const resetClientDetails = () => {
		setFirstName("");
		setLastName("");
		setdate_of_birth("");
	};

	const resetFlow = () => {
		setStep("email");
		setMessage("");
		setMessageVariant("info");
		setEmailError("");
		resetClientDetails();
	};

	const isValidEmail = (value) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
	};

	const navigateToClient = (client) => {
		if (client?.id) {
			navigate(`/client/${client.id}`);
			return;
		}

		navigate("/clients");
	};

	const handleCheckEmail = async (event) => {
		event.preventDefault();

		const emailValue = email.trim().toLowerCase();

		setEmail(emailValue);
		setEmailError("");
		setMessage("");

		if (!emailValue) {
			setEmailError("Email is required.");
			return;
		}

		if (!isValidEmail(emailValue)) {
			setEmailError("Please enter a valid email address.");
			return;
		}

		setIsCheckingEmail(true);

		try {
			const response = await api.post("/check-email/", {
				email: emailValue,
				timestamp: Date.now(),
			});

			if (response.data.exists && !response.data.is_client) {
				resetClientDetails();
				setStep("email");
				setMessage(response.data.message || "This email already belongs to a non-client account.");
				setMessageVariant("danger");
				return;
			}

			if (response.data.already_in_clinic) {
				resetClientDetails();
				setStep("email");
				setMessage(response.data.message || "This client is already in your clinic.");
				setMessageVariant("danger");
				return;
			}

			if (response.data.can_link) {
				resetClientDetails();
				setStep("existing");
				setMessage(response.data.message || "This client already exists and can be added to your clinic.");
				setMessageVariant("info");
				return;
			}

			setStep("new");
			setMessage("No existing client was found. Enter their details to create a new client account.");
			setMessageVariant("info");
		} catch (error) {
			if (error.response?.status === 429) {
				setEmailError("Too many attempts. Please try again later.");
				return;
			}

			const errorMessage =
				error.response?.data?.error ||
				error.response?.data?.detail ||
				"Unable to check this email. Please try again.";

			console.error("Email check error:", error.response ? error.response.data : error.message);
			setMessage(errorMessage);
			setMessageVariant("danger");
		} finally {
			setIsCheckingEmail(false);
		}
	};

	const handleLinkExistingClient = async () => {
		if (emailError) {
			setMessage("Please fix the email error before submitting.");
			setMessageVariant("danger");
			return;
		}

		setIsLoading(true);
		setMessage("");

		try {
			const response = await api.post("/users/add/", {
				email: email.trim().toLowerCase(),
			});

			const successMessage = response.data?.message || "Client linked to your clinic.";
			showAlert("success", successMessage);
			navigateToClient(response.data?.client);
		} catch (error) {
			const errorMessage =
				error.response?.data?.error ||
				error.response?.data?.detail ||
				"Failed to link client. Please try again.";

			console.error(error.response ? error.response.data : error.message);

			setMessage(errorMessage);
			setMessageVariant("danger");
			showAlert("danger", errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCreateNewClient = async (event) => {
		event.preventDefault();

		if (emailError) {
			setMessage("Please fix the email error before submitting.");
			setMessageVariant("danger");
			return;
		}

		setIsLoading(true);
		setMessage("");

		try {
			const response = await api.post("/users/add/", {
				first_name: firstName,
				last_name: lastName,
				date_of_birth,
				email: email.trim().toLowerCase(),
			});

			const successMessage = response.data?.message || "Client created and activation email sent.";
			showAlert("success", successMessage);
			navigateToClient(response.data?.client);
		} catch (error) {
			const errorMessage =
				error.response?.data?.error ||
				error.response?.data?.detail ||
				"Failed to create client. Please try again.";

			console.error(error.response ? error.response.data : error.message);

			setMessage(errorMessage);
			setMessageVariant("danger");
			showAlert("danger", errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Container className="d-flex justify-content-center align-items-center">
			<div className="row w-100 justify-content-center">
				<div className="col-12 col-md-6">
					<Card.Body data-walkthrough="activate-client">
						<h2 className="text-center mb-4">Create Client</h2>

						{message && (
							<Alert variant={messageVariant}>
								{message}
							</Alert>
						)}

						{step === "email" && (
							<Form onSubmit={handleCheckEmail}>
								<Form.Group className="mb-3" controlId="formEmail">
									<Form.Label>Client Email</Form.Label>
									<Form.Control
										type="email"
										name="email"
										placeholder="Enter client email"
										value={email}
										onChange={(event) => {
											setEmail(event.target.value);
											setEmailError("");
											setMessage("");
										}}
										isInvalid={!!emailError}
										required
									/>

									<Form.Control.Feedback type="invalid">
										{emailError}
									</Form.Control.Feedback>

									<Form.Text className="text-muted">
										We’ll check whether this client already exists before asking for more details.
									</Form.Text>
								</Form.Group>

								<Button
									variant="primary"
									type="submit"
									className="w-100 mb-5"
									disabled={isCheckingEmail}
								>
									{isCheckingEmail ? (
										<>
											<span
												className="spinner-border spinner-border-sm me-2"
												role="status"
												aria-hidden="true"
											></span>
											Checking...
										</>
									) : (
										"Check Client"
									)}
								</Button>
							</Form>
						)}

						{step === "existing" && (
							<div>
								<div className="mb-3">
									<h5 className="mb-2">Existing Client Found</h5>
									<p className="text-muted mb-0">
										This will add the existing client to your clinic. Their personal details will not be changed.
									</p>
								</div>

								<Button
									variant="primary"
									type="button"
									className="w-100 mb-3"
									disabled={isLoading}
									onClick={handleLinkExistingClient}
								>
									{isLoading ? (
										<>
											<span
												className="spinner-border spinner-border-sm me-2"
												role="status"
												aria-hidden="true"
											></span>
											Adding...
										</>
									) : (
										"Add Client to Clinic"
									)}
								</Button>

								<Button
									variant="outline-secondary"
									type="button"
									className="w-100 mb-5"
									disabled={isLoading}
									onClick={resetFlow}
								>
									Use Different Email
								</Button>
							</div>
						)}

						{step === "new" && (
							<Form onSubmit={handleCreateNewClient}>
								<div className="mb-3">
									<p className="mb-1">
										<strong>Email:</strong> {email}
									</p>
									<Button
										variant="link"
										type="button"
										className="p-0"
										disabled={isLoading}
										onClick={resetFlow}
									>
										Use different email
									</Button>
								</div>

								<Form.Group className="mb-3" controlId="formFirstName">
									<Form.Control
										type="text"
										name="first_name"
										placeholder="First Name"
										value={firstName}
										onChange={(event) => setFirstName(event.target.value)}
										required
									/>
								</Form.Group>

								<Form.Group className="mb-3" controlId="formLastName">
									<Form.Control
										type="text"
										name="last_name"
										placeholder="Last Name"
										value={lastName}
										onChange={(event) => setLastName(event.target.value)}
										required
									/>
								</Form.Group>

								<Form.Group className="mb-3" controlId="formdate_of_birth">
									<h6>Date of Birth</h6>
									<Form.Control
										type="date"
										placeholder="Date of Birth"
										label="Date of Birth"
										name="date_of_birth"
										value={date_of_birth}
										onChange={(event) => setdate_of_birth(event.target.value)}
										required
									/>
								</Form.Group>


								<Button
									variant="primary"
									type="submit"
									className="w-100 mb-5"
									disabled={isLoading}
								>
									{isLoading ? (
										<>
											<span
												className="spinner-border spinner-border-sm me-2"
												role="status"
												aria-hidden="true"
											></span>
											Creating...
										</>
									) : (
										"Create Client"
									)}
								</Button>
							</Form>
						)}
					</Card.Body>
				</div>
			</div>
		</Container>
	);
};

export default CreateClient;